import { Fragment, useState } from "react";
import { ChevronDown, Ruler } from "lucide-react";
import {
  editQuoteDimension, dimensionSummary,
  type QuantityDerivation,
} from "@workspace/api-zod/quantity-dimensions";
import { deriveCostRecipe, materialExtendedPrice, type CostRecipe } from "@workspace/api-zod/cost-recipe";
import { lineMaterialRows } from "@workspace/api-zod/materials-list";
import { matchesDimensionLabel } from "@workspace/api-zod/dimension-pricing";
import { materialProposalBasis, materialsApproved, materialSpecificationNeedsReview, recipeProcurementSafe, scopeNeedsNoPurchaseMaterials, validMaterialAllowance, type MaterialAllowanceApproval } from "@workspace/api-zod/material-allowance";
import { MaterialAllowanceControl, PurchaseMaterialSummary } from "./material-allowance-control";
import { useAutomaticMaterials } from "./use-automatic-materials";
import { dimensionRows, dimensionDisplayValue, dimensionStoredValue, formatDimension } from "./quote-dimension-display";
import { ensureLengthDimensions, steelSectionsFromEvidence, isSteelMemberWork, workDimensionHeading } from "@workspace/api-zod/dimension-pricing";
import "./quote-materials-workspace.css";
import { NewFeatureBadge } from "./new-feature-badge";

function SourceBadge({ source }: { source: string }) {
  return <span className={`inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 ${source === "assumed" ? "bg-amber-100 text-amber-800" : source === "measured" ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground"}`}>
    <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${source === "assumed" ? "bg-amber-500" : source === "measured" ? "bg-green-600" : "bg-slate-500"}`} />{source}
  </span>;
}

export function QuoteDimensions({ data: inputData, quantity, unit, specification, recipe, itemName, onChange }: {
  data?: QuantityDerivation | null; quantity: number; unit: string;
  specification?: string;
  itemName?: string;
  recipe?: CostRecipe | null;
  onChange: (data: QuantityDerivation, quantity: number) => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [editingUnit, setEditingUnit] = useState("m");
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState<{ id: string; message: string } | null>(null);
  const data = ensureLengthDimensions(inputData, unit, quantity, specification, recipe, { itemName });
  const steel = isSteelMemberWork(data, specification, recipe, itemName);
  const steelSections = steel ? steelSectionsFromEvidence(data, specification) : [];
  const separateAxes = /^(?:m[²2]|sqm|sq\.?\s*m|square\s*met(?:re|er)s?)$/i.test(unit.trim());
  const dimensions = [...(data?.dimensions ?? [])];
  const isSteelLength = (d: typeof dimensions[number]) => steelSections.length > 0 && d.unit === "m"
    && (/^(?:length|steel length)$/i.test(d.label.trim())
      || /\b(?:beam|steel|member)\s+(?:cut\s+)?length\s*$/i.test(d.label));
  // Presentation only: keep the original labels, ids and formula operands intact.
  if (steelSections.length) {
    const rank = (d: typeof dimensions[number]) => d.unit === "kg/m" ? 0 : isSteelLength(d) ? 1 : 2;
    dimensions.sort((a, b) => rank(a) - rank(b));
  }
  // Opening spans explain the cut length; they are not additional steel sizes.
  // Keep them as source evidence, unless quantity or a pricing model needs them.
  const pricingModels = recipe ? [
    recipe.labour_pricing, recipe.plant_pricing,
    recipe.shape === "specification" ? recipe.supply_pricing : undefined,
    ...recipe.materials.flatMap(material => [material.dimension_pricing, material.quantity_pricing]),
  ] : [];
  const normalLabel = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const isOpeningWidth = (d: typeof dimensions[number]) => d.unit === "m"
    && /\bopening\b.*\b(?:width|span)\s*$/i.test(d.label);
  const openingContext = dimensions.filter(d =>
    steel && dimensions.some(isSteelLength) && isOpeningWidth(d)
    && (data?.quantity_op === "direct" || data?.quantity_op === "count")
    && !data.quantity_operands.includes(d.id)
    && !pricingModels.some(model => model?.dimension_labels.some(label =>
      normalLabel(d.label) === normalLabel(label) || normalLabel(d.label).endsWith(` ${normalLabel(label)}`)
      || /\bopening\b.*\b(?:width|span)\s*$/i.test(label))));
  const visibleDimensions = dimensions.filter(d => !openingContext.includes(d));
  return <section className="min-w-0" aria-label="Dimensions">
    <details className="group/dimensions" data-testid="quote-dimensions">
    <summary className="inline-flex max-w-full cursor-pointer list-none items-center gap-1.5 rounded-md border border-dashed px-2.5 py-1.5 text-xs [&::-webkit-details-marker]:hidden">
      <Ruler aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
      <span>Dimensions</span>
      <ChevronDown aria-hidden="true" className="h-3 w-3 shrink-0 text-muted-foreground transition-transform group-open/dimensions:rotate-180" />
    </summary>
    <div className="mt-2 rounded-md border bg-card/50 p-3 space-y-2 min-w-0">
    {steelSections.map(section => {
      // Only evidence containing THIS designation can establish its source.
      // A measured length/weight alone does not prove the section came from files.
      const evidence = dimensions.filter(d => steelSectionsFromEvidence(
        { ...data!, dimensions: [d] }).includes(section));
      const sources = [...new Set(evidence.map(d => d.source))];
      const source = sources.length === 1 ? sources[0] : "source unconfirmed";
      return <details key={section} className="group text-xs border-b pb-2" data-testid="dimension-steel-section">
        <summary className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
          <span><span className="font-medium">Steel size – </span>{section.replace(/X/g, " × ").replace(/(UB|UC)$/, " $1")}</span>
          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
            <SourceBadge source={source} />
            <ChevronDown aria-hidden="true" className="h-3 w-3 transition-transform group-open:rotate-180" />
          </span>
        </summary>
        <div className="mt-2 space-y-1 text-muted-foreground">
          {source === "source unconfirmed" && <p>The section is shown in the item description, but its drawing source is not confirmed by the retained measurement evidence.</p>}
          {evidence.map(d => <p key={d.id} className="break-words">{d.note}</p>)}
        </div>
      </details>;
    })}
    {data?.quantity_op === "sum_products" && <p className="text-xs text-muted-foreground break-words"
      data-testid="construction-calculation">{dimensionSummary(data, quantity, unit)}</p>}
    {!data?.dimensions.length && <p className="text-xs text-muted-foreground" data-testid="dimensions-unavailable">
      No dimensions were recorded for this item. Quantity: {formatDimension(quantity)} {unit}.
    </p>}
    {dimensionRows(visibleDimensions, separateAxes).map(row => {
      const first = row.dimensions[0];
      const rowLabel = steelSections.length && first.unit === "kg/m" ? "Steel weight"
        : isSteelLength(first) ? "Steel length"
        : steel && isOpeningWidth(first) ? "Opening width" : workDimensionHeading(row.label, steel);
      // Keep the editing unit stable even if an edit crosses the display-unit boundary.
      const displayUnit = row.dimensions.some(d => d.id === editing) ? editingUnit : row.displayUnit;
      const sources = [...new Set(row.dimensions.map(d => d.source))];
      return <div key={first.id} className="text-xs border-b last:border-b-0 pb-2" data-testid={`dimension-row-${first.id}`}>
      <details className="group min-w-0">
        <summary className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 cursor-pointer list-none [&::-webkit-details-marker]:hidden"
          data-testid={`dimension-details-${first.id}`} aria-label={`Source details for ${rowLabel}`}>
          <span className="flex flex-wrap items-center gap-x-1.5 min-w-0">
            <span className="font-medium">{rowLabel} <span aria-hidden="true">–</span></span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
            {row.dimensions.map((d, index) => <Fragment key={d.id}>
              {index > 0 && <span aria-hidden="true"> × </span>}
        {editing === d.id ? <input id={`dimension-${d.id}`} aria-label={d.label} type="number" min="0" step="any"
          aria-describedby={`dimension-unit-${first.id}`} data-testid={`input-dimension-${d.id}`}
          inputMode="decimal" autoFocus className="h-8 w-20 rounded border bg-background px-2 text-sm"
          value={drafts[d.id] ?? String(dimensionDisplayValue(d.value, d.unit, displayUnit))} aria-invalid={error?.id === d.id}
          onClick={event => event.stopPropagation()}
          onBlur={() => { if (!error) { setEditing(null); setDrafts({}); } }}
          onKeyDown={event => { if (event.key === "Enter") event.currentTarget.blur(); }}
          onChange={event => {
            const raw = event.target.value;
            setDrafts(old => ({ ...old, [d.id]: raw }));
            try {
              if (!raw.trim()) throw new Error("Enter a non-negative, finite dimension");
              if (!data) return;
              const result = editQuoteDimension(data, d.id, dimensionStoredValue(Number(raw), d.unit, displayUnit), unit, quantity);
              setError(null); onChange(result.derivation, result.quantity);
            } catch (e) {
              const message = e instanceof Error ? e.message : "Invalid dimension";
              const opening = message.startsWith("Openings exceed") ? data?.dimensions.find(x => /^Opening\b/i.test(x.label)) : null;
              setError({ id: opening?.id ?? d.id, message: message.startsWith("[") ? "Enter a non-negative, finite dimension" : message });
            }
          }} />
          : <button type="button" id={`dimension-${d.id}`} className="underline decoration-dotted underline-offset-4 text-primary py-1"
            aria-label={`Edit ${d.label}`} aria-describedby={`dimension-unit-${first.id}`} data-testid={`edit-dimension-${d.id}`}
            onClick={event => { event.stopPropagation(); setEditingUnit(displayUnit); setEditing(d.id); setDrafts({}); }}>
            {formatDimension(dimensionDisplayValue(d.value, d.unit, displayUnit))}
          </button>}
            </Fragment>)}
            <span id={`dimension-unit-${first.id}`}> {displayUnit}</span>
            </span>
          </span>
          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          {sources.map(source => <SourceBadge key={source} source={source} />)}
          <ChevronDown aria-hidden="true" className="h-3 w-3 transition-transform group-open:rotate-180" />
          </span>
        </summary>
        <div className="mt-2 space-y-2 text-muted-foreground" data-testid={`dimension-sources-${first.id}`}>
        {isSteelLength(first) && first.id === dimensions.find(isSteelLength)?.id
          && openingContext.map(d => <div key={d.id} data-testid={`steel-opening-evidence-${d.id}`}>
          <span className="font-medium">Related opening width (not steel cut length): </span>
          {formatDimension(dimensionDisplayValue(d.value, d.unit, d.value < 1 ? "mm" : "m"))} {d.value < 1 ? "mm" : "m"}
          <span className="ml-2"><SourceBadge source={d.source} /></span>
          <p className="break-words">{d.label}</p>
          {d.note && <p className="break-words">{d.note}</p>}
        </div>)}
        {row.dimensions.map(d => <div key={d.id}>
          <span className="font-medium">{d.label}</span>
          <span className="ml-2">{d.source}</span>
          {d.source === "measured" && <span className="ml-2">measured from your files</span>}
          {d.source === "entered" && d.originalValue != null && <p>was {formatDimension(dimensionDisplayValue(d.originalValue, d.unit, displayUnit))} {displayUnit} · {d.originalSource}</p>}
          {d.note && <p className="mt-0.5 break-words">{d.note}</p>}
        </div>)}
        </div>
      </details>
      {row.dimensions.map(d => error?.id === d.id && <p key={d.id} role="alert" className="text-destructive mt-1">{error.message}</p>)}
    </div>;
    })}
    {data?.note.includes("Material price review:") && <p role="status" className="text-xs text-amber-700">
      Dimensions changed. Price retained because no material calculation is configured; review the supply cost.
    </p>}
    {data?.quantity_op === "direct" && /^(?:m[²³23]|sqm|sq\s*m|m3)$/i.test(unit.trim())
      && data.dimensions.some(d => d.originalValue != null && d.originalValue !== d.value) && <p role="status" className="text-xs text-amber-700">
      Size saved, but these dimensions do not reconcile with the priced quantity. Review the quantity before sending.
    </p>}
    {data?.quantity_overridden && <p className="text-xs text-muted-foreground">Quantity set separately. Material calculations still use these measurements.</p>}
    </div>
    </details>
  </section>;
}

export function QuoteLineMaterials({ recipe, quantity }: { recipe: CostRecipe; quantity: number }) {
  const detail = deriveCostRecipe(recipe).recipe;
  return <div className="mt-3 border-t pt-2 text-xs space-y-1" data-testid="quote-extended-materials">
    <p className="font-medium">Materials for this line · quantity {quantity.toFixed(2)}</p>
    {detail.shape === "specification" && <p>Supply: {quantity.toFixed(2)} × £{Number(detail.supply_allowance ?? 0).toFixed(2)} = £{materialExtendedPrice(quantity, detail.supply_allowance ?? 0)}</p>}
    {detail.materials.map(m => {
      const perUnit = m.quantity_per_unit ?? m.base_factor;
      const extended = Math.round(perUnit * quantity * 10000) / 10000;
      return <p key={m.material_id} className="break-words">{m.name}: {quantity.toFixed(2)} × {perUnit} = {extended} {m.unit} · £{materialExtendedPrice(extended, m.unit_price)}</p>;
    })}
    {!detail.materials.length && detail.shape !== "specification" && <p className="text-muted-foreground">No material inputs in this recipe.</p>}
    <p className="font-medium border-t pt-1">Materials total £{(lineMaterialRows({
      name: "", category: "", unit: "each", unitCost: 0, quantity, quoteCostRecipe: detail,
    }, 0).reduce((n, row) => n + Math.round(row.exactTotal * 100), 0) / 100).toFixed(2)}</p>
  </div>;
}

/** Presentation only: the original derivation and recipe remain intact. */
function reviewMeasurementGroups(data: QuantityDerivation | null | undefined, recipe: CostRecipe | null | undefined) {
  if (!data) return { quantity: [], pricing: [], context: [] };
  const quantityIds = new Set(data.quantity_op === "direct" || data.quantity_overridden ? [] : data.quantity_operands);
  const models = recipe ? [
    recipe.labour_pricing, recipe.plant_pricing,
    recipe.shape === "specification" ? recipe.supply_pricing : undefined,
    ...recipe.materials.flatMap(material => [material.dimension_pricing, material.quantity_pricing]),
  ] : [];
  // A recipe label identifies a pricing reference only when it maps to one
  // distinct dimension. Ambiguous matches stay in the source evidence.
  const pricingIds = new Set(models.flatMap(model => (model?.dimension_labels ?? []).flatMap(label => {
    const matches = data.dimensions.filter(d => matchesDimensionLabel(d.label, label));
    return matches.length === 1 ? [matches[0].id] : [];
  })));
  return {
    quantity: data.dimensions.filter(d => quantityIds.has(d.id)),
    pricing: data.dimensions.filter(d => !quantityIds.has(d.id) && pricingIds.has(d.id)),
    context: data.dimensions.filter(d => !quantityIds.has(d.id) && !pricingIds.has(d.id)),
  };
}

export function QuantityMaterialsReview({ items, onAllowanceChange, onJump, autoSuggest = false, sessionKey }: { items: {
  name: string; quantity: number; unit: string; quantityDerivation?: QuantityDerivation | null;
  quoteCostRecipe?: CostRecipe | null; isOptional?: boolean;
  category?: string; description?: string | null; unitCost?: number | string; materialCost?: number | string | null;
  materialAllowanceApproval?: unknown;
  costLibraryTaskId?: string | null;
}[]; onAllowanceChange?: (index: number, approval: MaterialAllowanceApproval | null) => void; onJump?: (index: number) => void; autoSuggest?: boolean; sessionKey?: string }) {
  const lines = items.map(item => ({ ...item, category: item.category ?? "general", unitCost: item.unitCost ?? 0 }));
  const automatic = useAutomaticMaterials(lines, onAllowanceChange, autoSuggest, sessionKey);
  const attention: number[] = [], ready: number[] = [];
  items.forEach((item, index) => {
    if (scopeNeedsNoPurchaseMaterials(item)) return;
    const line = { ...item, category: item.category ?? "general", unitCost: item.unitCost ?? 0 };
    const proposal = validMaterialAllowance(line);
    const hasBreakdown = (item.quoteCostRecipe && recipeProcurementSafe(line)) || proposal;
    // A confirmed zero-material breakdown needs no purchasing review.
    // Missing/unsafe breakdowns remain visible: unknown is not zero.
    const pendingProposal = proposal && !materialsApproved(line, true);
    if (hasBreakdown && !pendingProposal && lineMaterialRows(line, index)
      .reduce((sum, row) => sum + Math.round(row.exactTotal * 100), 0) === 0) return;
    if (!hasBreakdown || (materialSpecificationNeedsReview(line) && !materialsApproved(line, true))) attention.push(index);
    else if (pendingProposal || !materialsApproved(line, true)) ready.push(index);
  });
  if (!items.length || (!onAllowanceChange && !items.some(i => i.quantityDerivation || i.quoteCostRecipe))) return null;
  const renderRows = (indices: number[]) => indices.map(index => {
      const item = items[index];
      const allowanceLine = { ...item, category: item.category ?? "general", unitCost: item.unitCost ?? 0 };
      const approved = validMaterialAllowance(allowanceLine);
      const libraryMaterials = approved?.source === "library"
        || (recipeProcurementSafe(allowanceLine) && !approved);
      const measurements = reviewMeasurementGroups(item.quantityDerivation, item.quoteCostRecipe);
      const assumed = [...measurements.quantity, ...measurements.pricing].filter(d => d.source === "assumed").length;
      const basis = materialProposalBasis(allowanceLine);
      const isApproved = materialsApproved(allowanceLine, true);
      const materialTotal = libraryMaterials || approved
        ? lineMaterialRows(allowanceLine, index)
          .reduce((sum, row) => sum + Math.round(row.exactTotal * 100), 0) / 100 : null;
      return <article key={`${index}:${item.name}`} className="qm-review-row">
        <div className="qm-review-summary">
          <div className="qm-review-check">
             {materialTotal !== null && !isApproved && onAllowanceChange && <input type="checkbox"
              aria-label={`Approve materials for ${item.name}`} checked={isApproved}
              onChange={event => {
                automatic.startManual(basis);
                onAllowanceChange(index, approved
                  ? { ...approved, mode: "automatic", source: "builder", basis, approved: event.target.checked,
                    manualApproved: event.target.checked, builderWithheld: false }
                  : { version: 1, mode: "automatic", source: "builder", recipeApproval: true,
                    basis, amount: materialTotal, approved: event.target.checked,
                    manualApproved: event.target.checked, builderWithheld: false });
              }} />}
            <span>{item.name}{item.isOptional && <span className="ml-2 text-[10px] font-normal text-amber-700">Optional</span>}</span>
            {materialTotal !== null && !isApproved && <span className="qm-approval-status">
              Awaiting approval
            </span>}
          </div>
          <div className="qm-review-numbers">
            <strong>{item.quantity.toLocaleString("en-GB", { maximumFractionDigits: 2 })} {item.unit}</strong>
            <small>{materialTotal === null ? automatic.states[basis] || (onAllowanceChange
              ? "Material not matched – please enter manually." : "Material breakdown unavailable. Edit the quote to add materials.")
              : `${libraryMaterials ? "Cost Library materials" : "Suggested Materials Price"} £${materialTotal.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</small>
          </div>
        </div>
        {autoSuggest && !isApproved && <button
          type="button" className="text-xs underline text-primary" onClick={() => automatic.retry(basis)}
          disabled={/^(?:Checking|Identifying)/.test(automatic.states[basis] ?? "")}
          data-testid={`button-retry-materials-${index}`}>Retry material lookup</button>}
        {assumed > 0 && <span className="qm-review-assumed">{assumed} assumed {assumed === 1 ? "measurement" : "measurements"} to check</span>}
        {materialSpecificationNeedsReview(allowanceLine) && <p className="qm-review-assumed">Material specification needs checking against the work description.</p>}
        {onAllowanceChange && <MaterialAllowanceControl key={basis}
          line={allowanceLine} status={automatic.states[basis]} onManualStart={() => automatic.startManual(basis)}
          onChange={approval => onAllowanceChange(index, approval)} />}
        {!onAllowanceChange && <details className="qm-purchase-editor"><summary>Materials</summary>
          <PurchaseMaterialSummary line={allowanceLine} />
        </details>}
        {lineMaterialRows(allowanceLine, index).map(row => <div className="qm-material-name qm-review-material-source" key={row.key}>
          <strong>{row.name}</strong>
          <details className="qm-sources">
            <summary>Spec &amp; sources <ChevronDown aria-hidden="true" size={12} /></summary>
            <div>
              {row.spec && <p>{row.spec}</p>}
              <p>Used in: {onJump
                ? <button type="button" className="underline underline-offset-2 hover:text-primary" onClick={() => onJump(index)}>{item.name}</button>
                : item.name}</p>
            </div>
          </details>
        </div>)}
      </article>;
    });
  return <details className="qm-panel qm-review-panel" data-testid="quantity-materials-review">
    <summary className="qm-panel-heading flex items-center gap-2 flex-wrap">
      <span className="qm-step" aria-hidden="true">01</span>
      <span className="qm-panel-title">Materials &amp; Pricing Review</span>
      <span className="qm-heading-meta">
      <NewFeatureBadge testId="badge-materials-review-new" />
      <span className="qm-private-tag">Builder only</span>
      </span>
       <ChevronDown className="qm-heading-chevron ml-auto" size={14} aria-hidden="true" />
    </summary>
    <p className="qm-scope-note">Complete materials attached to work items are included automatically in the Materials List. Only missing details, confirmation requests and flagged issues appear here. Selling prices stay unchanged.</p>
    <div className="qm-review-list">
      <section className="qm-ready-group">
        <h5 className="qm-ready-heading">Needs Review ({ready.length + attention.length})</h5>
        <p className="qm-ready-note">Materials awaiting confirmation or correction are listed here.</p>
        {ready.length + attention.length ? renderRows([...ready, ...attention]) : <p className="qm-review-empty">No materials awaiting confirmation.</p>}
      </section>
    </div>
  </details>;
}
